import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar"
import EventBusyIcon from "@mui/icons-material/EventBusy"

import MainLayout from "../../layouts/MainLayout"

import {
  cancelDeliveryTrip,
  completeDeliveryTrip,
  createDeliveryTrip,
  deliverTripStop,
  getDeliveryTripById,
  getDeliveryTripFormData,
  getDeliveryTrips,
  returnDeliveryTrip,
  startDeliveryTrip,
} from "../../services/deliveryTripService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10

const DEFAULT_FUEL_PRICE_CDF = 3500
const DEFAULT_EXCHANGE_RATE_CDF_USD = 2800


const defaultForm = {
  stock_order_id: "",
  vehicle_id: "",
  driver_id: "",
  source_warehouse_id: "",
  estimated_distance_km: "",
  fuel_price_per_liter_cdf: String(
    DEFAULT_FUEL_PRICE_CDF
  ),
  exchange_rate_cdf_usd: String(
    DEFAULT_EXCHANGE_RATE_CDF_USD
  ),
  notes: "",
}


const statusLabels = {
  planned: "Planifiée",
  vehicle_out: "Véhicule sorti",
  in_progress: "En cours",
  completed: "Livraisons terminées",
  returned: "Véhicule retourné",
  cancelled: "Annulée",
}


const statusColors = {
  planned: "info",
  vehicle_out: "secondary",
  in_progress: "primary",
  completed: "success",
  returned: "success",
  cancelled: "default",
}


const stopStatusLabels = {
  planned: "Prévu",
  in_progress: "En cours",
  delivered: "Livré",
  failed: "Échec",
  cancelled: "Annulé",
}


const stopStatusColors = {
  planned: "info",
  in_progress: "primary",
  delivered: "success",
  failed: "error",
  cancelled: "default",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatKm(value) {
  return `${formatAmount(value)} km`
}


function formatLiters(value) {
  return `${formatAmount(value)} L`
}


function formatCDF(value) {
  return `${formatAmount(value)} FC`
}


function formatUSD(value) {
  return `${formatAmount(value)} $`
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-"
  }

  return date.toLocaleString(
    "fr-FR"
  )
}


function getEstimatedFuelCostCdf(
  trip
) {
  return toNumber(
    trip?.estimated_fuel_cost_cdf ||
      trip?.estimated_fuel_cost
  )
}


function getActualFuelCostCdf(
  trip
) {
  return toNumber(
    trip?.actual_fuel_cost_cdf ||
      trip?.actual_fuel_cost
  )
}


function getFuelCostVarianceCdf(
  trip
) {
  if (
    trip?.fuel_cost_variance_cdf !==
      undefined &&
    trip?.fuel_cost_variance_cdf !==
      null
  ) {
    return toNumber(
      trip.fuel_cost_variance_cdf
    )
  }

  if (
    !trip?.has_actual_logistics &&
    trip?.status !== "returned"
  ) {
    return 0
  }

  return (
    getActualFuelCostCdf(
      trip
    ) -
    getEstimatedFuelCostCdf(
      trip
    )
  )
}


function getFuelCostVarianceUsd(
  trip
) {
  if (
    trip?.fuel_cost_variance_usd !==
      undefined &&
    trip?.fuel_cost_variance_usd !==
      null
  ) {
    return toNumber(
      trip.fuel_cost_variance_usd
    )
  }

  if (
    !trip?.has_actual_logistics &&
    trip?.status !== "returned"
  ) {
    return 0
  }

  return (
    toNumber(
      trip?.actual_fuel_cost_usd
    ) -
    toNumber(
      trip?.estimated_fuel_cost_usd
    )
  )
}


function getDistanceVarianceKm(
  trip
) {
  if (
    trip?.distance_variance_km !==
      undefined &&
    trip?.distance_variance_km !==
      null
  ) {
    return toNumber(
      trip.distance_variance_km
    )
  }

  if (
    !trip?.has_actual_logistics &&
    trip?.status !== "returned"
  ) {
    return 0
  }

  return (
    toNumber(
      trip?.actual_distance_km
    ) -
    toNumber(
      trip?.estimated_distance_km
    )
  )
}


function getFuelLitersVariance(
  trip
) {
  if (
    trip?.fuel_liters_variance !==
      undefined &&
    trip?.fuel_liters_variance !==
      null
  ) {
    return toNumber(
      trip.fuel_liters_variance
    )
  }

  if (
    !trip?.has_actual_logistics &&
    trip?.status !== "returned"
  ) {
    return 0
  }

  return (
    toNumber(
      trip?.actual_fuel_liters
    ) -
    toNumber(
      trip?.estimated_fuel_liters
    )
  )
}


function formatSignedAmount(
  value,
  suffix = ""
) {
  const number =
    toNumber(value)

  const sign =
    number > 0
      ? "+"
      : ""

  return `${sign}${formatAmount(
    number
  )}${suffix}`
}


function varianceColor(value) {
  const number =
    toNumber(value)

  if (number > 0) {
    return "error.main"
  }

  if (number < 0) {
    return "success.main"
  }

  return "text.primary"
}


function getApiErrorMessage(
  error,
  fallback = "Action impossible."
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.4,
          sm: 1.6,
        },

        py: {
          xs: 1.2,
          sm: 1.3,
        },

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.2,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,

            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.75,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"

            color="text.secondary"

            sx={{
              display: "block",

              mt: 0.35,

              whiteSpace: "nowrap",

              overflow: "hidden",

              textOverflow: "ellipsis",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// FUEL SUMMARY
// =========================================================

function FuelSummaryBox({
  fuel,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,

        borderRadius: 3,

        bgcolor: "#f8fafc",
      }}
    >
      <Typography
        fontWeight={900}
        color="#0f172a"
        sx={{
          mb: 1,
        }}
      >
        Estimation carburant
      </Typography>


      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",

            sm:
              "repeat(3, minmax(0, 1fr))",
          },

          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Carburant estimé
          </Typography>

          <Typography
            fontWeight={900}
          >
            {formatLiters(
              fuel.liters
            )}
          </Typography>
        </Box>


        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Coût estimé FC
          </Typography>

          <Typography
            fontWeight={900}
          >
            {formatCDF(
              fuel.costCdf
            )}
          </Typography>
        </Box>


        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Coût estimé USD
          </Typography>

          <Typography
            fontWeight={900}
          >
            {formatUSD(
              fuel.costUsd
            )}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function DeliveryTripsPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    trips,
    setTrips,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({})

  const [
    formData,
    setFormData,
  ] = useState({
    vehicles: [],
    drivers: [],
    warehouses: [],
    stores: [],
    stock_orders: [],
    permissions: {},
    defaults: {},
  })


  // =======================================================
  // UI
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)


  // =======================================================
  // CREATE
  // =======================================================

  const [
    openCreate,
    setOpenCreate,
  ] = useState(false)

  const [
    form,
    setForm,
  ] = useState({
    ...defaultForm,
  })


  // =======================================================
  // DETAIL
  // =======================================================

  const [
    detailOpen,
    setDetailOpen,
  ] = useState(false)

  const [
    detail,
    setDetail,
  ] = useState({
    trip: null,
    stops: [],
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    filters,
    setFilters,
  ] = useState({
    search: "",
    status: "",
  })


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    page,
    setPage,
  ] = useState(0)


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const permissions =
    formData.permissions || {}


  // =======================================================
  // SELECTED FORM OBJECTS
  // =======================================================

  const selectedVehicle =
    useMemo(() => {
      return (
        formData.vehicles.find(
          (vehicle) =>
            String(
              vehicle.id
            ) ===
            String(
              form.vehicle_id
            )
        ) ||
        null
      )
    }, [
      form.vehicle_id,
      formData.vehicles,
    ])


  const selectedDriver =
    useMemo(() => {
      return (
        formData.drivers.find(
          (driver) =>
            String(
              driver.id
            ) ===
            String(
              form.driver_id
            )
        ) ||
        null
      )
    }, [
      form.driver_id,
      formData.drivers,
    ])


  const selectedStockOrder =
    useMemo(() => {
      return (
        formData.stock_orders.find(
          (order) =>
            String(
              order.id
            ) ===
            String(
              form.stock_order_id
            )
        ) ||
        null
      )
    }, [
      form.stock_order_id,
      formData.stock_orders,
    ])


  const selectedWarehouse =
    useMemo(() => {
      return (
        formData.warehouses.find(
          (warehouse) =>
            String(
              warehouse.id
            ) ===
            String(
              form.source_warehouse_id
            )
        ) ||
        null
      )
    }, [
      form.source_warehouse_id,
      formData.warehouses,
    ])


  // =======================================================
  // ESTIMATED FUEL
  // =======================================================

  const estimatedFuel =
    useMemo(() => {
      const distance =
        toNumber(
          form.estimated_distance_km
        )

      const consumption =
        toNumber(
          selectedVehicle?.fuel_consumption_l_100km
        )

      const priceCdf =
        toNumber(
          form.fuel_price_per_liter_cdf
        )

      const exchangeRate =
        toNumber(
          form.exchange_rate_cdf_usd
        )

      const liters =
        (
          distance *
          consumption
        ) /
        100

      const costCdf =
        liters *
        priceCdf

      const costUsd =
        exchangeRate > 0
          ? costCdf /
            exchangeRate
          : 0

      return {
        liters,
        costCdf,
        costUsd,
      }
    }, [
      form.estimated_distance_km,
      form.fuel_price_per_liter_cdf,
      form.exchange_rate_cdf_usd,
      selectedVehicle,
    ])


  // =======================================================
  // PAGINATED TRIPS
  // =======================================================

  const paginatedTrips =
    useMemo(() => {
      const start =
        page *
        ROWS_PER_PAGE

      return trips.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      trips,
      page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,

        Math.ceil(
          trips.length /
            ROWS_PER_PAGE
        )
      )

    if (
      page >= totalPages
    ) {
      setPage(
        totalPages - 1
      )
    }
  }, [
    trips.length,
    page,
  ])


  // =======================================================
  // SNACKBAR
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const closeSnackbar = () => {
    setSnackbar(
      (current) => ({
        ...current,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getDeliveryTripFormData()

        setFormData({
          vehicles:
            data.vehicles || [],

          drivers:
            data.drivers || [],

          warehouses:
            data.warehouses || [],

          stores:
            data.stores || [],

          stock_orders:
            data.stock_orders || [],

          permissions:
            data.permissions || {},

          defaults:
            data.defaults || {},
        })


        setForm(
          (current) => ({
            ...current,

            fuel_price_per_liter_cdf:
              String(
                data?.defaults
                  ?.fuel_price_per_liter_cdf ||
                  DEFAULT_FUEL_PRICE_CDF
              ),

            exchange_rate_cdf_usd:
              String(
                data?.defaults
                  ?.exchange_rate_cdf_usd ||
                  DEFAULT_EXCHANGE_RATE_CDF_USD
              ),
          })
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les données de la tournée."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // LOAD TRIPS
  // =======================================================

  const loadTrips =
    async (
      customFilters = null
    ) => {
      try {
        setLoading(true)

        const params =
          customFilters ||
          filters

        const data =
          await getDeliveryTrips(
            params
          )

        setTrips(
          Array.isArray(
            data?.items
          )
            ? data.items
            : []
        )

        setSummary(
          data?.summary || {}
        )

        setPage(0)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les tournées."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadFormData()

    loadTrips({
      search: "",
      status: "",
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const updateForm = (
    field,
    value
  ) => {
    setForm(
      (current) => ({
        ...current,

        [field]:
          value,
      })
    )
  }


  const resetForm = () => {
    setForm({
      ...defaultForm,

      fuel_price_per_liter_cdf:
        String(
          formData?.defaults
            ?.fuel_price_per_liter_cdf ||
            DEFAULT_FUEL_PRICE_CDF
        ),

      exchange_rate_cdf_usd:
        String(
          formData?.defaults
            ?.exchange_rate_cdf_usd ||
            DEFAULT_EXCHANGE_RATE_CDF_USD
        ),
    })
  }


  const openCreateDialog =
    () => {
      resetForm()

      setOpenCreate(
        true
      )
    }


  const closeCreateDialog =
    () => {
      if (saving) {
        return
      }

      setOpenCreate(
        false
      )

      resetForm()
    }


  const handleStockOrderChange =
    (value) => {
      const order =
        formData.stock_orders.find(
          (item) =>
            String(
              item.id
            ) ===
            String(
              value
            )
        )

      setForm(
        (current) => ({
          ...current,

          stock_order_id:
            value,

          source_warehouse_id:
            current.source_warehouse_id ||
            order?.source_warehouse_id ||
            "",
        })
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (!form.vehicle_id) {
      showSnackbar(
        "Sélectionnez un véhicule.",
        "warning"
      )

      return false
    }

    if (!form.driver_id) {
      showSnackbar(
        "Sélectionnez un chauffeur.",
        "warning"
      )

      return false
    }

    if (!form.stock_order_id) {
      showSnackbar(
        "Sélectionnez une commande stock.",
        "warning"
      )

      return false
    }

    if (
      toNumber(
        selectedVehicle?.fuel_consumption_l_100km
      ) <= 0
    ) {
      showSnackbar(
        "Ce véhicule n'a pas de consommation L/100km valide. Corrigez sa fiche avant la tournée.",
        "warning"
      )

      return false
    }

    if (
      toNumber(
        form.estimated_distance_km
      ) <= 0
    ) {
      showSnackbar(
        "La distance estimée en km est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      toNumber(
        form.fuel_price_per_liter_cdf
      ) <= 0
    ) {
      showSnackbar(
        "Le prix du litre carburant en FC est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      toNumber(
        form.exchange_rate_cdf_usd
      ) <= 0
    ) {
      showSnackbar(
        "Le taux FC/USD est obligatoire.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // CREATE TRIP
  // =======================================================

  const handleCreate =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const fuelPriceCdf =
        Number(
          form.fuel_price_per_liter_cdf ||
            0
        )

      const exchangeRate =
        Number(
          form.exchange_rate_cdf_usd ||
            0
        )

      const payload = {
        stock_order_id:
          Number(
            form.stock_order_id
          ),

        vehicle_id:
          Number(
            form.vehicle_id
          ),

        driver_id:
          Number(
            form.driver_id
          ),

        source_warehouse_id:
          form.source_warehouse_id
            ? Number(
                form.source_warehouse_id
              )
            : null,

        estimated_distance_km:
          Number(
            form.estimated_distance_km ||
              0
          ),

        fuel_price_per_liter:
          fuelPriceCdf,

        fuel_price_per_liter_cdf:
          fuelPriceCdf,

        exchange_rate_cdf_usd:
          exchangeRate,

        notes:
          form.notes?.trim() ||
          null,

        stops: [],
      }

      try {
        setSaving(true)

        const result =
          await createDeliveryTrip(
            payload
          )

        showSnackbar(
          result?.trip_code
            ? `Tournée créée : ${result.trip_code}`
            : "Tournée créée avec succès."
        )

        /*
         * Fermeture directe :
         * closeCreateDialog() ne peut pas fermer
         * pendant saving === true.
         */
        setOpenCreate(
          false
        )

        resetForm()

        await loadFormData()
        await loadTrips()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de créer la tournée."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DETAIL
  // =======================================================

  const openDetail =
    async (tripId) => {
      try {
        const data =
          await getDeliveryTripById(
            tripId
          )

        setDetail({
          trip:
            data.trip || null,

          stops:
            data.stops || [],
        })

        setDetailOpen(
          true
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger le détail."
          ),
          "error"
        )
      }
    }


  const refreshDetail =
    async () => {
      if (
        !detail.trip?.id
      ) {
        return
      }

      const data =
        await getDeliveryTripById(
          detail.trip.id
        )

      setDetail({
        trip:
          data.trip || null,

        stops:
          data.stops || [],
      })
    }


  // =======================================================
  // TRIP ACTION
  // =======================================================

  const runTripAction =
    async (
      trip,
      label,
      action
    ) => {
      const notes =
        window.prompt(
          `${label} - commentaire optionnel :`
        )

      if (
        notes === null
      ) {
        return
      }

      try {
        setSaving(true)

        const result =
          await action({
            notes:
              notes?.trim() ||
              null,
          })

        showSnackbar(
          result?.message ||
            `${label} effectué.`
        )

        await loadFormData()
        await loadTrips()

        if (
          detail.trip?.id
        ) {
          await refreshDetail()
        }
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // RETURN
  // =======================================================

  const runReturnAction =
    async (trip) => {
      const actualDistance =
        window.prompt(
          "Retour véhicule - distance réelle en km. Exemple : 10"
        )

      if (
        actualDistance ===
        null
      ) {
        return
      }

      const actualFuel =
        window.prompt(
          "Carburant réel consommé en litres. Exemple : 2 donnera 7 000 FC si le litre est à 3 500 FC."
        )

      if (
        actualFuel === null
      ) {
        return
      }

      const fuelPrice =
        window.prompt(
          "Prix réel du litre en FC :",
          String(
            trip.fuel_price_per_liter_cdf ||
              trip.fuel_price_per_liter ||
              DEFAULT_FUEL_PRICE_CDF
          )
        )

      if (
        fuelPrice === null
      ) {
        return
      }

      const exchangeRate =
        window.prompt(
          "Taux FC/USD :",
          String(
            trip.exchange_rate_cdf_usd ||
              DEFAULT_EXCHANGE_RATE_CDF_USD
          )
        )

      if (
        exchangeRate ===
        null
      ) {
        return
      }


      const parsedDistance =
        Number(
          actualDistance
        )

      const parsedFuel =
        Number(
          actualFuel
        )

      const parsedFuelPrice =
        Number(
          fuelPrice
        )

      const parsedExchangeRate =
        Number(
          exchangeRate
        )


      if (
        !Number.isFinite(
          parsedDistance
        ) ||
        parsedDistance <= 0
      ) {
        showSnackbar(
          "La distance réelle doit être supérieure à 0.",
          "warning"
        )

        return
      }


      if (
        !Number.isFinite(
          parsedFuel
        ) ||
        parsedFuel < 0
      ) {
        showSnackbar(
          "La quantité réelle de carburant est invalide.",
          "warning"
        )

        return
      }


      if (
        !Number.isFinite(
          parsedFuelPrice
        ) ||
        parsedFuelPrice <= 0
      ) {
        showSnackbar(
          "Le prix réel du litre est invalide.",
          "warning"
        )

        return
      }


      if (
        !Number.isFinite(
          parsedExchangeRate
        ) ||
        parsedExchangeRate <= 0
      ) {
        showSnackbar(
          "Le taux FC/USD est invalide.",
          "warning"
        )

        return
      }


      try {
        setSaving(true)

        const result =
          await returnDeliveryTrip(
            trip.id,
            {
              actual_distance_km:
                parsedDistance,

              actual_fuel_liters:
                parsedFuel,

              fuel_price_per_liter_cdf:
                parsedFuelPrice,

              exchange_rate_cdf_usd:
                parsedExchangeRate,
            }
          )

        showSnackbar(
          result?.actual_fuel_cost_cdf
            ? `Retour confirmé. Coût carburant : ${formatCDF(
                result.actual_fuel_cost_cdf
              )} / ${formatUSD(
                result.actual_fuel_cost_usd
              )}`
            : result?.message ||
                "Retour confirmé."
        )

        await loadFormData()
        await loadTrips()

        if (
          detail.trip?.id
        ) {
          await refreshDetail()
        }
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELIVER STOP
  // =======================================================

  const handleDeliverStop =
    async (stop) => {
      const notes =
        window.prompt(
          "Livraison arrêt - commentaire optionnel :"
        )

      if (
        notes === null
      ) {
        return
      }

      if (
        !detail.trip?.id
      ) {
        return
      }

      try {
        setSaving(true)

        const result =
          await deliverTripStop(
            detail.trip.id,
            stop.id,
            {
              notes:
                notes?.trim() ||
                null,
            }
          )

        showSnackbar(
          result?.message ||
            "Arrêt livré."
        )

        await loadTrips()
        await refreshDetail()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // FILTER RESET
  // =======================================================

  const resetFilters =
    async () => {
      const emptyFilters = {
        search: "",
        status: "",
      }

      setFilters(
        emptyFilters
      )

      setPage(0)

      await loadTrips(
        emptyFilters
      )
    }


  // =======================================================
  // ACTION RULES
  // =======================================================

  const canStart =
    (trip) =>
      trip.status ===
      "planned"


  const canComplete =
    (trip) =>
      [
        "vehicle_out",
        "in_progress",
      ].includes(
        trip.status
      )


  const canReturn =
    (trip) =>
      [
        "vehicle_out",
        "in_progress",
        "completed",
      ].includes(
        trip.status
      )


  const canCancel =
    (trip) =>
      ![
        "returned",
        "cancelled",
      ].includes(
        trip.status
      )


  // =======================================================
  // ACTION BUTTONS
  // =======================================================

  const renderTripActions =
    (
      trip,
      mobile = false
    ) => {
      return (
        <Box
          sx={{
            display: "flex",

            flexDirection:
              mobile
                ? "column"
                : "row",

            flexWrap: "wrap",

            gap: 0.65,

            alignItems:
              mobile
                ? "stretch"
                : "center",
          }}
        >
          <Button
            size="small"

            variant="contained"

            disableElevation

            onClick={() =>
              openDetail(
                trip.id
              )
            }
          >
            Détail
          </Button>


          {canStart(
            trip
          ) && (
            <Button
              size="small"

              color="secondary"

              variant="outlined"

              disabled={
                saving
              }

              onClick={() =>
                runTripAction(
                  trip,
                  "Sortie véhicule",
                  (payload) =>
                    startDeliveryTrip(
                      trip.id,
                      payload
                    )
                )
              }
            >
              Sortie
            </Button>
          )}


          {canComplete(
            trip
          ) && (
            <Button
              size="small"

              color="success"

              variant="outlined"

              disabled={
                saving
              }

              onClick={() =>
                runTripAction(
                  trip,
                  "Terminer livraisons",
                  (payload) =>
                    completeDeliveryTrip(
                      trip.id,
                      payload
                    )
                )
              }
            >
              Terminer
            </Button>
          )}


          {canReturn(
            trip
          ) && (
            <Button
              size="small"

              color="primary"

              variant="outlined"

              disabled={
                saving
              }

              onClick={() =>
                runReturnAction(
                  trip
                )
              }
            >
              Retour
            </Button>
          )}


          {canCancel(
            trip
          ) && (
            <Button
              size="small"

              color="error"

              variant="outlined"

              disabled={
                saving
              }

              onClick={() =>
                runTripAction(
                  trip,
                  "Annulation tournée",
                  (payload) =>
                    cancelDeliveryTrip(
                      trip.id,
                      payload
                    )
                )
              }
            >
              Annuler
            </Button>
          )}
        </Box>
      )
    }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight: 800,

              borderRadius: 2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            alignItems: "start",

            gap: 1.5,

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Tournées / Livraisons
            </Typography>


            <Typography
              variant="body2"

              color="text.secondary"

              sx={{
                mt: 0.4,

                maxWidth: 850,

                lineHeight: 1.5,
              }}
            >
              Planification des sorties véhicules,
              chauffeurs, destinations, distances et coûts
              réels ou estimés du carburant.
            </Typography>
          </Box>


          <Button
            variant="contained"

            disableElevation

            onClick={
              openCreateDialog
            }

            disabled={
              !permissions.can_create
            }

            sx={{
              minHeight: 40,

              minWidth: {
                xs: "100%",
                sm: 180,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouvelle tournée
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"

          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,

            alignItems: "center",

            bgcolor:
              "rgba(14, 165, 233, 0.025)",
          }}
        >
          L'estimation utilise la distance prévue, la
          consommation du véhicule et le prix du litre.
          Au retour, RLSys compare automatiquement les valeurs
          estimées aux valeurs réelles.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.15,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Tournées"

            value={
              summary.total ||
              0
            }

            caption="Total enregistré"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <LocalShippingIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Planifiées"

            value={
              summary.planned ||
              0
            }

            caption="En attente de départ"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <EventBusyIcon fontSize="small" />
            }
          />


          <MetricCard
            label="En cours"

            value={
              summary.in_progress ||
              0
            }

            caption="Sur le terrain"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <DirectionsCarIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Retournées"

            value={
              summary.returned ||
              0
            }

            caption="Tournées clôturées"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 6px 18px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(250px, 1.5fr) minmax(180px, 0.7fr) auto auto",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              size="small"

              label="Recherche"

              placeholder="Code tournée, plaque, chauffeur..."

              value={
                filters.search
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    search:
                      event.target.value,
                  })
                )
              }

              fullWidth
            />


            <TextField
              select

              size="small"

              label="Statut"

              value={
                filters.status
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    status:
                      event.target.value,
                  })
                )
              }

              fullWidth
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {Object.entries(
                statusLabels
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <MenuItem
                    key={
                      value
                    }

                    value={
                      value
                    }
                  >
                    {label}
                  </MenuItem>
                )
              )}
            </TextField>


            <Button
              variant="contained"

              disableElevation

              disabled={
                loading
              }

              onClick={() =>
                loadTrips()
              }

              sx={{
                minHeight: 40,

                minWidth: 95,
              }}
            >
              Appliquer
            </Button>


            <Button
              variant="outlined"

              disabled={
                loading
              }

              onClick={
                resetFilters
              }

              sx={{
                minHeight: 40,

                minWidth: 115,
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            TRIPS REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >

          {/* HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}

                color="#0f172a"
              >
                Registre des tournées
              </Typography>

              <Typography
                variant="caption"

                color="text.secondary"
              >
                Suivi des livraisons, affectations et
                performances logistiques
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${trips.length} tournée${
                trips.length > 1
                  ? "s"
                  : ""
              }`}

              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <>
              <Box
                sx={{
                  width: "100%",

                  overflowX: "auto",
                }}
              >
                <Table
                  size="small"

                  sx={{
                    minWidth: 1080,

                    "& .MuiTableCell-head":
                      {
                        bgcolor: "#f8fafc",

                        fontWeight: 900,

                        color: "#334155",

                        fontSize:
                          "0.77rem",

                        whiteSpace:
                          "nowrap",
                      },

                    "& .MuiTableCell-body":
                      {
                        py: 1,

                        borderColor:
                          "rgba(148, 163, 184, 0.14)",
                      },

                    "& .MuiTableRow-root:hover":
                      {
                        bgcolor:
                          "#f8fbff",
                      },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Tournée
                      </TableCell>

                      <TableCell>
                        Affectation
                      </TableCell>

                      <TableCell>
                        Départ
                      </TableCell>

                      <TableCell>
                        Statut
                      </TableCell>

                      <TableCell>
                        Estimation
                      </TableCell>

                      <TableCell>
                        Réel
                      </TableCell>

                      <TableCell>
                        Écart coût
                      </TableCell>

                      <TableCell>
                        Arrêts
                      </TableCell>

                      <TableCell>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>


                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}

                          align="center"

                          sx={{
                            py: 5,
                          }}
                        >
                          Chargement...
                        </TableCell>
                      </TableRow>
                    ) : paginatedTrips.length ===
                      0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}

                          align="center"

                          sx={{
                            py: 5,
                          }}
                        >
                          Aucune tournée trouvée.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTrips.map(
                        (trip) => {
                          const hasActual =
                            trip.has_actual_logistics ||
                            trip.status ===
                              "returned"

                          return (
                            <TableRow
                              key={
                                trip.id
                              }
                            >

                              {/* TRIP */}

                              <TableCell>
                                <Typography
                                  variant="body2"

                                  fontWeight={900}

                                  color="#0f172a"

                                  noWrap
                                >
                                  {trip.trip_code ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  display="block"

                                  noWrap
                                >
                                  Cmd.{" "}
                                  {trip.order_code ||
                                    "-"}
                                </Typography>
                              </TableCell>


                              {/* ASSIGNMENT */}

                              <TableCell>
                                <Typography
                                  variant="body2"

                                  fontWeight={800}

                                  noWrap
                                >
                                  {trip.plate_number ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  display="block"

                                  noWrap
                                >
                                  {trip.driver_name ||
                                    "Chauffeur N/D"}
                                </Typography>
                              </TableCell>


                              {/* WAREHOUSE */}

                              <TableCell>
                                <Typography
                                  variant="body2"

                                  fontWeight={700}

                                  noWrap
                                >
                                  {trip.warehouse_code ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  display="block"

                                  noWrap
                                >
                                  {trip.warehouse_name ||
                                    ""}
                                </Typography>
                              </TableCell>


                              {/* STATUS */}

                              <TableCell>
                                <Chip
                                  size="small"

                                  label={
                                    statusLabels[
                                      trip.status
                                    ] ||
                                    trip.status
                                  }

                                  color={
                                    statusColors[
                                      trip.status
                                    ] ||
                                    "default"
                                  }

                                  sx={{
                                    fontWeight:
                                      800,
                                  }}
                                />
                              </TableCell>


                              {/* ESTIMATE */}

                              <TableCell>
                                <Typography
                                  variant="body2"

                                  fontWeight={800}

                                  noWrap
                                >
                                  {formatKm(
                                    trip.estimated_distance_km
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  display="block"

                                  noWrap
                                >
                                  {formatLiters(
                                    trip.estimated_fuel_liters
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  display="block"

                                  noWrap
                                >
                                  {formatCDF(
                                    getEstimatedFuelCostCdf(
                                      trip
                                    )
                                  )}
                                </Typography>
                              </TableCell>


                              {/* ACTUAL */}

                              <TableCell>
                                {hasActual ? (
                                  <>
                                    <Typography
                                      variant="body2"

                                      fontWeight={800}

                                      noWrap
                                    >
                                      {formatKm(
                                        trip.actual_distance_km
                                      )}
                                    </Typography>

                                    <Typography
                                      variant="caption"

                                      color="text.secondary"

                                      display="block"

                                      noWrap
                                    >
                                      {formatLiters(
                                        trip.actual_fuel_liters
                                      )}
                                    </Typography>

                                    <Typography
                                      variant="caption"

                                      color="text.secondary"

                                      display="block"

                                      noWrap
                                    >
                                      {formatCDF(
                                        getActualFuelCostCdf(
                                          trip
                                        )
                                      )}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography
                                    variant="body2"

                                    color="text.secondary"
                                  >
                                    —
                                  </Typography>
                                )}
                              </TableCell>


                              {/* VARIANCE */}

                              <TableCell>
                                {hasActual ? (
                                  <>
                                    <Typography
                                      variant="body2"

                                      fontWeight={900}

                                      color={varianceColor(
                                        getFuelCostVarianceCdf(
                                          trip
                                        )
                                      )}

                                      noWrap
                                    >
                                      {formatSignedAmount(
                                        getFuelCostVarianceCdf(
                                          trip
                                        ),
                                        " FC"
                                      )}
                                    </Typography>

                                    <Typography
                                      variant="caption"

                                      color={varianceColor(
                                        getDistanceVarianceKm(
                                          trip
                                        )
                                      )}

                                      display="block"

                                      noWrap
                                    >
                                      {formatSignedAmount(
                                        getDistanceVarianceKm(
                                          trip
                                        ),
                                        " km"
                                      )}
                                    </Typography>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </TableCell>


                              {/* STOPS */}

                              <TableCell>
                                <Typography
                                  variant="body2"

                                  fontWeight={900}
                                >
                                  {trip.delivered_stops_count ||
                                    0}
                                  {" / "}
                                  {trip.stops_count ||
                                    0}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"
                                >
                                  livrés
                                </Typography>
                              </TableCell>


                              {/* ACTIONS */}

                              <TableCell>
                                {renderTripActions(
                                  trip
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        }
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>


              {/* DESKTOP PAGINATION */}

              <TablePagination
                component="div"

                count={
                  trips.length
                }

                page={
                  page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  borderTop:
                    "1px solid",

                  borderColor:
                    "divider",

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </>
          ) : (

            /* =================================================
               MOBILE / TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des tournées...
                  </Typography>
                </Box>
              ) : paginatedTrips.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune tournée trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.1}
                >
                  {paginatedTrips.map(
                    (trip) => {
                      const hasActual =
                        trip.has_actual_logistics ||
                        trip.status ===
                          "returned"

                      return (
                        <Paper
                          key={
                            trip.id
                          }

                          variant="outlined"

                          sx={{
                            p: {
                              xs: 1.4,
                              sm: 1.6,
                            },

                            borderRadius: 3,

                            bgcolor: "#ffffff",

                            borderColor:
                              "rgba(148, 163, 184, 0.22)",
                          }}
                        >
                          <Stack
                            spacing={1.3}
                          >

                            {/* TOP */}

                            <Box
                              sx={{
                                display: "flex",

                                justifyContent:
                                  "space-between",

                                alignItems:
                                  "flex-start",

                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  fontWeight={900}

                                  color="#0f172a"
                                >
                                  {trip.trip_code ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"
                                >
                                  Commande{" "}
                                  {trip.order_code ||
                                    "-"}
                                </Typography>
                              </Box>


                              <Chip
                                size="small"

                                label={
                                  statusLabels[
                                    trip.status
                                  ] ||
                                  trip.status
                                }

                                color={
                                  statusColors[
                                    trip.status
                                  ] ||
                                  "default"
                                }

                                sx={{
                                  fontWeight: 800,
                                }}
                              />
                            </Box>


                            {/* ASSIGNMENT */}

                            <Box
                              sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  fontWeight={700}
                                >
                                  Véhicule
                                </Typography>

                                <Typography
                                  variant="body2"

                                  fontWeight={900}
                                >
                                  {trip.plate_number ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"
                                >
                                  {trip.vehicle_brand ||
                                    ""}{" "}
                                  {trip.vehicle_model ||
                                    ""}
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  fontWeight={700}
                                >
                                  Chauffeur
                                </Typography>

                                <Typography
                                  variant="body2"

                                  fontWeight={900}
                                >
                                  {trip.driver_name ||
                                    "-"}
                                </Typography>
                              </Box>
                            </Box>


                            {/* COSTS */}

                            <Box
                              sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                                gap: 1,
                              }}
                            >
                              <Paper
                                variant="outlined"

                                sx={{
                                  p: 1,

                                  borderRadius: 2.5,

                                  bgcolor: "#f8fafc",
                                }}
                              >
                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  fontWeight={700}
                                >
                                  Estimation
                                </Typography>

                                <Typography
                                  variant="body2"

                                  fontWeight={900}
                                >
                                  {formatKm(
                                    trip.estimated_distance_km
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  display="block"
                                >
                                  {formatLiters(
                                    trip.estimated_fuel_liters
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"

                                  color="text.secondary"
                                >
                                  {formatCDF(
                                    getEstimatedFuelCostCdf(
                                      trip
                                    )
                                  )}
                                </Typography>
                              </Paper>


                              <Paper
                                variant="outlined"

                                sx={{
                                  p: 1,

                                  borderRadius: 2.5,

                                  bgcolor: "#f8fafc",
                                }}
                              >
                                <Typography
                                  variant="caption"

                                  color="text.secondary"

                                  fontWeight={700}
                                >
                                  Réel
                                </Typography>

                                {hasActual ? (
                                  <>
                                    <Typography
                                      variant="body2"

                                      fontWeight={900}
                                    >
                                      {formatKm(
                                        trip.actual_distance_km
                                      )}
                                    </Typography>

                                    <Typography
                                      variant="caption"

                                      color="text.secondary"

                                      display="block"
                                    >
                                      {formatLiters(
                                        trip.actual_fuel_liters
                                      )}
                                    </Typography>

                                    <Typography
                                      variant="caption"

                                      color={varianceColor(
                                        getFuelCostVarianceCdf(
                                          trip
                                        )
                                      )}
                                    >
                                      Écart :{" "}
                                      {formatSignedAmount(
                                        getFuelCostVarianceCdf(
                                          trip
                                        ),
                                        " FC"
                                      )}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography
                                    variant="body2"

                                    color="text.secondary"
                                  >
                                    Pas encore renseigné
                                  </Typography>
                                )}
                              </Paper>
                            </Box>


                            {/* STOPS */}

                            <Box
                              sx={{
                                display: "flex",

                                justifyContent:
                                  "space-between",

                                alignItems:
                                  "center",

                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"

                                color="text.secondary"
                              >
                                Arrêts livrés
                              </Typography>

                              <Chip
                                size="small"

                                variant="outlined"

                                label={`${trip.delivered_stops_count || 0} / ${trip.stops_count || 0}`}

                                sx={{
                                  fontWeight: 800,
                                }}
                              />
                            </Box>


                            {/* ACTIONS */}

                            {renderTripActions(
                              trip,
                              true
                            )}
                          </Stack>
                        </Paper>
                      )
                    }
                  )}
                </Stack>
              )}


              {/* MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  trips.length
                }

                page={
                  page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor: "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,

                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          CREATE DIALOG
      ================================================= */}

      <Dialog
        open={
          openCreate
        }

        onClose={
          closeCreateDialog
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography
            fontWeight={900}

            fontSize={20}

            color="#0f172a"
          >
            Nouvelle tournée / livraison
          </Typography>

          <Typography
            variant="body2"

            color="text.secondary"

            sx={{
              mt: 0.3,
            }}
          >
            Affectez une commande, un véhicule et un chauffeur,
            puis estimez la distance et le carburant nécessaire.
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >
            <Alert
              severity="info"

              variant="outlined"

              sx={{
                borderRadius: 3,
              }}
            >
              Coût estimé = distance × consommation véhicule
              ÷ 100 × prix du litre. Le montant USD est
              calculé avec le taux FC/USD.
            </Alert>


            {/* ORDER */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.2,
                }}
              >
                Commande à livrer
              </Typography>


              <Autocomplete
                options={
                  formData.stock_orders
                }

                value={
                  selectedStockOrder
                }

                onChange={(
                  _,
                  value
                ) =>
                  handleStockOrderChange(
                    value?.id ||
                      ""
                  )
                }

                disabled={
                  saving
                }

                getOptionLabel={(
                  order
                ) =>
                  order
                    ? `${order.order_code || ""} - ${order.store_code || ""} ${order.store_name || ""} | ${order.organization_name || ""}`
                    : ""
                }

                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  String(
                    option.id
                  ) ===
                  String(
                    value.id
                  )
                }

                renderInput={(
                  params
                ) => (
                  <TextField
                    {...params}

                    size="small"

                    label="Commande stock"

                    placeholder="Code commande, shop ou organisation..."

                    required
                  />
                )}

                fullWidth
              />
            </Paper>


            {/* AFFECTATION */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.2,
                }}
              >
                Affectation logistique
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <Autocomplete
                  options={
                    formData.vehicles
                  }

                  value={
                    selectedVehicle
                  }

                  onChange={(
                    _,
                    value
                  ) =>
                    updateForm(
                      "vehicle_id",
                      value?.id ||
                        ""
                    )
                  }

                  disabled={
                    saving
                  }

                  getOptionLabel={(
                    vehicle
                  ) =>
                    vehicle
                      ? `${vehicle.plate_number || ""} - ${vehicle.brand || ""} ${vehicle.model || ""} | ${vehicle.fuel_consumption_l_100km || 0} L/100km`
                      : ""
                  }

                  isOptionEqualToValue={(
                    option,
                    value
                  ) =>
                    String(
                      option.id
                    ) ===
                    String(
                      value.id
                    )
                  }

                  renderInput={(
                    params
                  ) => (
                    <TextField
                      {...params}

                      size="small"

                      label="Véhicule disponible"

                      placeholder="Plaque, marque ou modèle..."

                      required
                    />
                  )}

                  fullWidth
                />


                <Autocomplete
                  options={
                    formData.drivers
                  }

                  value={
                    selectedDriver
                  }

                  onChange={(
                    _,
                    value
                  ) =>
                    updateForm(
                      "driver_id",
                      value?.id ||
                        ""
                    )
                  }

                  disabled={
                    saving
                  }

                  getOptionLabel={(
                    driver
                  ) =>
                    driver
                      ? `${driver.full_name || ""} - ${driver.license_number || "Permis N/D"}`
                      : ""
                  }

                  isOptionEqualToValue={(
                    option,
                    value
                  ) =>
                    String(
                      option.id
                    ) ===
                    String(
                      value.id
                    )
                  }

                  renderInput={(
                    params
                  ) => (
                    <TextField
                      {...params}

                      size="small"

                      label="Chauffeur disponible"

                      placeholder="Nom du chauffeur..."

                      required
                    />
                  )}

                  fullWidth
                />
              </Box>


              <Autocomplete
                options={
                  formData.warehouses
                }

                value={
                  selectedWarehouse
                }

                onChange={(
                  _,
                  value
                ) =>
                  updateForm(
                    "source_warehouse_id",
                    value?.id ||
                      ""
                  )
                }

                disabled={
                  saving
                }

                getOptionLabel={(
                  warehouse
                ) =>
                  warehouse
                    ? `${warehouse.code || ""} - ${warehouse.name || ""}`
                    : ""
                }

                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  String(
                    option.id
                  ) ===
                  String(
                    value.id
                  )
                }

                renderInput={(
                  params
                ) => (
                  <TextField
                    {...params}

                    size="small"

                    label="Entrepôt de départ"

                    placeholder="Code ou nom de l'entrepôt..."
                  />
                )}

                fullWidth
              />
            </Paper>


            {/* ESTIMATION */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.2,
                }}
              >
                Distance et carburant
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(3, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Distance estimée km"

                  type="number"

                  value={
                    form.estimated_distance_km
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "estimated_distance_km",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.1,
                    },
                  }}
                />


                <TextField
                  size="small"

                  label="Prix litre carburant FC"

                  type="number"

                  value={
                    form.fuel_price_per_liter_cdf
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "fuel_price_per_liter_cdf",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 1,
                    },
                  }}
                />


                <TextField
                  size="small"

                  label="Taux FC / USD"

                  type="number"

                  value={
                    form.exchange_rate_cdf_usd
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "exchange_rate_cdf_usd",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 1,
                    },
                  }}
                />
              </Box>
            </Paper>


            <FuelSummaryBox
              fuel={
                estimatedFuel
              }
            />


            <TextField
              label="Notes"

              value={
                form.notes
              }

              onChange={(
                event
              ) =>
                updateForm(
                  "notes",
                  event.target.value
                )
              }

              multiline

              minRows={3}

              fullWidth

              disabled={
                saving
              }
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            borderTop:
              "1px solid",

            borderColor:
              "divider",

            gap: 1,
          }}
        >
          <Button
            onClick={
              closeCreateDialog
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"

            disableElevation

            onClick={
              handleCreate
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : "Créer tournée"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          DETAIL DIALOG
      ================================================= */}

      <Dialog
        open={
          detailOpen
        }

        onClose={() =>
          setDetailOpen(
            false
          )
        }

        fullWidth

        maxWidth="lg"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "94dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography
            fontWeight={900}

            fontSize={20}

            color="#0f172a"
          >
            Détail tournée{" "}
            {detail.trip?.trip_code ||
              ""}
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          {!detail.trip ? (
            <Typography>
              Chargement...
            </Typography>
          ) : (
            <Stack
              spacing={2}
            >

              {/* GENERAL */}

              <Paper
                variant="outlined"

                sx={{
                  p: 1.5,

                  borderRadius: 3,
                }}
              >
                <Box
                  sx={{
                    display: "grid",

                    gridTemplateColumns: {
                      xs: "1fr",

                      sm:
                        "repeat(2, minmax(0, 1fr))",

                      lg:
                        "repeat(5, minmax(0, 1fr))",
                    },

                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"

                      color="text.secondary"
                    >
                      Statut
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.4,
                      }}
                    >
                      <Chip
                        size="small"

                        label={
                          statusLabels[
                            detail.trip
                              .status
                          ] ||
                          detail.trip
                            .status
                        }

                        color={
                          statusColors[
                            detail.trip
                              .status
                          ] ||
                          "default"
                        }

                        sx={{
                          fontWeight: 800,
                        }}
                      />
                    </Box>
                  </Box>


                  <Box>
                    <Typography
                      variant="caption"

                      color="text.secondary"
                    >
                      Véhicule
                    </Typography>

                    <Typography
                      fontWeight={900}
                    >
                      {detail.trip
                        .plate_number ||
                        "-"}
                    </Typography>
                  </Box>


                  <Box>
                    <Typography
                      variant="caption"

                      color="text.secondary"
                    >
                      Chauffeur
                    </Typography>

                    <Typography
                      fontWeight={900}
                    >
                      {detail.trip
                        .driver_name ||
                        "-"}
                    </Typography>
                  </Box>


                  <Box>
                    <Typography
                      variant="caption"

                      color="text.secondary"
                    >
                      Départ
                    </Typography>

                    <Typography
                      variant="body2"

                      fontWeight={800}
                    >
                      {formatDateTime(
                        detail.trip
                          .departure_time
                      )}
                    </Typography>
                  </Box>


                  <Box>
                    <Typography
                      variant="caption"

                      color="text.secondary"
                    >
                      Retour
                    </Typography>

                    <Typography
                      variant="body2"

                      fontWeight={800}
                    >
                      {formatDateTime(
                        detail.trip
                          .return_time
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>


              {/* COMPARISON */}

              <Paper
                variant="outlined"

                sx={{
                  p: 1.5,

                  borderRadius: 3,

                  bgcolor: "#f8fafc",
                }}
              >
                <Typography
                  fontWeight={900}

                  color="#0f172a"

                  sx={{
                    mb: 1.3,
                  }}
                >
                  Comparaison logistique
                </Typography>


                <Box
                  sx={{
                    overflowX: "auto",
                  }}
                >
                  <Table
                    size="small"

                    sx={{
                      minWidth: 650,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Indicateur
                        </TableCell>

                        <TableCell align="right">
                          Estimé
                        </TableCell>

                        <TableCell align="right">
                          Réel
                        </TableCell>

                        <TableCell align="right">
                          Écart
                        </TableCell>
                      </TableRow>
                    </TableHead>


                    <TableBody>
                      <TableRow>
                        <TableCell>
                          Distance
                        </TableCell>

                        <TableCell align="right">
                          {formatKm(
                            detail.trip
                              .estimated_distance_km
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {formatKm(
                            detail.trip
                              .actual_distance_km
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Typography
                            fontWeight={900}

                            color={varianceColor(
                              getDistanceVarianceKm(
                                detail.trip
                              )
                            )}
                          >
                            {formatSignedAmount(
                              getDistanceVarianceKm(
                                detail.trip
                              ),
                              " km"
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>


                      <TableRow>
                        <TableCell>
                          Carburant
                        </TableCell>

                        <TableCell align="right">
                          {formatLiters(
                            detail.trip
                              .estimated_fuel_liters
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {formatLiters(
                            detail.trip
                              .actual_fuel_liters
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Typography
                            fontWeight={900}

                            color={varianceColor(
                              getFuelLitersVariance(
                                detail.trip
                              )
                            )}
                          >
                            {formatSignedAmount(
                              getFuelLitersVariance(
                                detail.trip
                              ),
                              " L"
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>


                      <TableRow>
                        <TableCell>
                          Coût carburant FC
                        </TableCell>

                        <TableCell align="right">
                          {formatCDF(
                            getEstimatedFuelCostCdf(
                              detail.trip
                            )
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {formatCDF(
                            getActualFuelCostCdf(
                              detail.trip
                            )
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Typography
                            fontWeight={900}

                            color={varianceColor(
                              getFuelCostVarianceCdf(
                                detail.trip
                              )
                            )}
                          >
                            {formatSignedAmount(
                              getFuelCostVarianceCdf(
                                detail.trip
                              ),
                              " FC"
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>


                      <TableRow>
                        <TableCell>
                          Coût carburant USD
                        </TableCell>

                        <TableCell align="right">
                          {formatUSD(
                            detail.trip
                              .estimated_fuel_cost_usd
                          )}
                        </TableCell>

                        <TableCell align="right">
                          {formatUSD(
                            detail.trip
                              .actual_fuel_cost_usd
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Typography
                            fontWeight={900}

                            color={varianceColor(
                              getFuelCostVarianceUsd(
                                detail.trip
                              )
                            )}
                          >
                            {formatSignedAmount(
                              getFuelCostVarianceUsd(
                                detail.trip
                              ),
                              " $"
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>


                <Divider
                  sx={{
                    my: 1.5,
                  }}
                />


                <Typography
                  variant="body2"

                  color="text.secondary"
                >
                  Taux utilisé :{" "}
                  <strong>
                    {formatAmount(
                      detail.trip
                        .exchange_rate_cdf_usd
                    )}{" "}
                    FC / 1 USD
                  </strong>
                </Typography>
              </Paper>


              {/* STOPS */}

              <Box>
                <Typography
                  variant="h6"

                  fontWeight={900}

                  color="#0f172a"
                >
                  Arrêts de livraison
                </Typography>

                <Typography
                  variant="body2"

                  color="text.secondary"

                  sx={{
                    mb: 1.2,
                  }}
                >
                  Suivi des destinations et confirmations
                  de livraison.
                </Typography>


                {isSmDown ? (
                  <Stack
                    spacing={1}
                  >
                    {detail.stops.length ===
                    0 ? (
                      <Typography
                        color="text.secondary"
                      >
                        Aucun arrêt.
                      </Typography>
                    ) : (
                      detail.stops.map(
                        (stop) => {
                          const effectiveStatus =
                            stop.effective_status ||
                            stop.delivery_status ||
                            stop.status

                          return (
                            <Paper
                              key={
                                stop.id
                              }

                              variant="outlined"

                              sx={{
                                p: 1.3,

                                borderRadius: 3,
                              }}
                            >
                              <Stack
                                spacing={1}
                              >
                                <Box
                                  sx={{
                                    display: "flex",

                                    justifyContent:
                                      "space-between",

                                    gap: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      fontWeight={900}
                                    >
                                      {stop.stop_label ||
                                        stop.store_name ||
                                        stop.customer_name ||
                                        "-"}
                                    </Typography>

                                    <Typography
                                      variant="caption"

                                      color="text.secondary"
                                    >
                                      Arrêt{" "}
                                      {stop.stop_order ||
                                        "-"}{" "}
                                      • Commande{" "}
                                      {stop.order_code ||
                                        "-"}
                                    </Typography>
                                  </Box>


                                  <Chip
                                    size="small"

                                    label={
                                      stopStatusLabels[
                                        effectiveStatus
                                      ] ||
                                      effectiveStatus
                                    }

                                    color={
                                      stopStatusColors[
                                        effectiveStatus
                                      ] ||
                                      "default"
                                    }
                                  />
                                </Box>


                                <Typography
                                  variant="body2"

                                  color="text.secondary"
                                >
                                  {stop.address_text ||
                                    "Adresse non renseignée"}
                                </Typography>


                                {stop.delivered_at ? (
                                  <Typography
                                    variant="caption"

                                    color="text.secondary"
                                  >
                                    Livré le :{" "}
                                    {formatDateTime(
                                      stop.delivered_at
                                    )}
                                  </Typography>
                                ) : null}


                                {effectiveStatus !==
                                  "delivered" &&
                                  detail.trip
                                    .status !==
                                    "returned" &&
                                  detail.trip
                                    .status !==
                                    "cancelled" && (
                                    <Button
                                      size="small"

                                      variant="contained"

                                      color="success"

                                      disableElevation

                                      disabled={
                                        saving
                                      }

                                      onClick={() =>
                                        handleDeliverStop(
                                          stop
                                        )
                                      }
                                    >
                                      Marquer livré
                                    </Button>
                                  )}
                              </Stack>
                            </Paper>
                          )
                        }
                      )
                    )}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      overflowX:
                        "auto",
                    }}
                  >
                    <Table
                      size="small"

                      sx={{
                        minWidth: 850,
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            Ordre
                          </TableCell>

                          <TableCell>
                            Commande
                          </TableCell>

                          <TableCell>
                            Destination
                          </TableCell>

                          <TableCell>
                            Adresse
                          </TableCell>

                          <TableCell>
                            Statut
                          </TableCell>

                          <TableCell>
                            Livré le
                          </TableCell>

                          <TableCell>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>


                      <TableBody>
                        {detail.stops.length ===
                        0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}

                              align="center"

                              sx={{
                                py: 4,
                              }}
                            >
                              Aucun arrêt.
                            </TableCell>
                          </TableRow>
                        ) : (
                          detail.stops.map(
                            (stop) => {
                              const effectiveStatus =
                                stop.effective_status ||
                                stop.delivery_status ||
                                stop.status

                              return (
                                <TableRow
                                  key={
                                    stop.id
                                  }
                                >
                                  <TableCell>
                                    {stop.stop_order}
                                  </TableCell>

                                  <TableCell>
                                    {stop.order_code ||
                                      "-"}
                                  </TableCell>

                                  <TableCell>
                                    {stop.stop_label ||
                                      stop.store_name ||
                                      stop.customer_name ||
                                      "-"}
                                  </TableCell>

                                  <TableCell>
                                    {stop.address_text ||
                                      "-"}
                                  </TableCell>

                                  <TableCell>
                                    <Chip
                                      size="small"

                                      label={
                                        stopStatusLabels[
                                          effectiveStatus
                                        ] ||
                                        effectiveStatus
                                      }

                                      color={
                                        stopStatusColors[
                                          effectiveStatus
                                        ] ||
                                        "default"
                                      }
                                    />
                                  </TableCell>

                                  <TableCell>
                                    {formatDateTime(
                                      stop.delivered_at
                                    )}
                                  </TableCell>

                                  <TableCell>
                                    {effectiveStatus !==
                                      "delivered" &&
                                      detail.trip
                                        .status !==
                                        "returned" &&
                                      detail.trip
                                        .status !==
                                        "cancelled" && (
                                        <Button
                                          size="small"

                                          variant="contained"

                                          color="success"

                                          disableElevation

                                          disabled={
                                            saving
                                          }

                                          onClick={() =>
                                            handleDeliverStop(
                                              stop
                                            )
                                          }
                                        >
                                          Marquer livré
                                        </Button>
                                      )}
                                  </TableCell>
                                </TableRow>
                              )
                            }
                          )
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={() =>
              setDetailOpen(
                false
              )
            }
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          5000
        }

        onClose={
          closeSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }

          onClose={
            closeSnackbar
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default DeliveryTripsPage